// Shared helpers + UI for the free-preview flow.
// Users with access_status === "preview" have a 4-day countdown from preview_started_at.
// After that the app shows the "Preview ended" screen everywhere.

import { useState, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, Sparkles } from "lucide-react";

const CHECKOUT_URL = "https://YOUR-KAJABI-CHECKOUT-URL"; // TODO: replace with real Kajabi checkout

export function previewStatus(user) {
  if (!user || user.access_status !== "preview") {
    return { isPreview: false, isExpired: false, daysLeft: 999, hoursLeft: 999 * 24 };
  }
  const started = user.preview_started_at ? new Date(user.preview_started_at) : null;
  if (!started || isNaN(started.getTime())) return { isPreview: true, isExpired: false, daysLeft: 4, hoursLeft: 96 };
  const now = new Date();
  const msLeft = 4 * 24 * 60 * 60 * 1000 - (now - started);
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
  const hoursLeft = Math.max(0, Math.ceil(msLeft / (60 * 60 * 1000)));
  return { isPreview: true, isExpired: msLeft <= 0, daysLeft, hoursLeft };
}

const UnlockCtx = createContext(null);
export function useUnlockModal() { return useContext(UnlockCtx); }

export function PreviewProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <UnlockCtx.Provider value={{ open: (r) => { setReason(r || ""); setOpen(true); } }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-50 bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="unlock-modal">
          <div className="bg-warm-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-2"><Lock className="text-mauve" size={22} /><h2 className="font-display text-2xl font-bold text-navy">Unlock your full journey</h2></div>
            <p className="text-ink-mid mt-2">{reason || "This part of the program opens when you enroll. Your preview data is saved and picks up right where you left off."}</p>
            <a href={CHECKOUT_URL} target="_blank" rel="noreferrer" data-testid="unlock-checkout" className="mt-6 inline-flex items-center gap-2 bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light">
              <Sparkles size={16} /> Enroll now
            </a>
            <button onClick={() => setOpen(false)} className="ml-3 text-ink-mid text-sm">Maybe later</button>
          </div>
        </div>
      )}
    </UnlockCtx.Provider>
  );
}

export function PreviewBanner({ user }) {
  const s = previewStatus(user);
  if (!s.isPreview || s.isExpired) return null;
  const isFinalDay = s.daysLeft <= 1;
  return (
    <div
      data-testid="preview-banner"
      className={`mb-5 rounded-2xl px-5 py-4 flex items-center justify-between gap-3 flex-wrap ${
        isFinalDay ? "bg-mauve text-warm-white border-2 border-mauve" : "bg-cream border border-gold text-ink-dark"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isFinalDay ? "bg-warm-white/20" : "bg-gold"}`}>
          {isFinalDay ? <Sparkles size={16} className="text-warm-white" /> : <Lock size={16} className="text-navy" />}
        </div>
        <div>
          <div className={`text-xs uppercase tracking-widest font-bold ${isFinalDay ? "text-warm-white/80" : "text-mauve"}`}>Free preview</div>
          <div className="font-display font-bold text-lg" data-testid="preview-countdown">
            {isFinalDay
              ? (s.hoursLeft <= 12 ? "Last chance — your preview closes tonight" : "Final day — your preview closes tomorrow")
              : `${s.daysLeft} day${s.daysLeft === 1 ? "" : "s"} left to unlock at this price`}
          </div>
        </div>
      </div>
      <a href={CHECKOUT_URL} target="_blank" rel="noreferrer" data-testid="preview-banner-cta" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold ${isFinalDay ? "bg-warm-white text-mauve" : "bg-navy text-warm-white hover:bg-navy-deep"}`}>
        Enroll now →
      </a>
    </div>
  );
}

export function PreviewEndedScreen() {
  return (
    <div className="min-h-screen shell-bg flex items-center justify-center p-6" data-testid="preview-ended">
      <div className="max-w-lg bg-warm-white border border-edge rounded-3xl p-10 text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-mauve-pale flex items-center justify-center mb-4"><Lock className="text-mauve" size={26} /></div>
        <h1 className="font-display text-3xl font-bold text-navy">Your preview has ended</h1>
        <p className="text-ink-mid mt-3 leading-relaxed">
          Your free preview window has closed, but nothing is lost — your tribe, your answers, and everything you started are saved. Enroll now to pick up exactly where you left off.
        </p>
        <a href={CHECKOUT_URL} target="_blank" rel="noreferrer" data-testid="preview-ended-checkout" className="mt-6 inline-flex items-center gap-2 bg-gold text-ink-dark px-6 py-3 rounded-full font-bold hover:bg-gold-light">
          <Sparkles size={16} /> Enroll now
        </a>
        <div className="mt-6 text-xs text-ink-soft">Already paid? Your admin will flip your access shortly — no action needed on your end.</div>
      </div>
    </div>
  );
}

/**
 * Wraps a route/page and shows the "Preview ended" screen if the user's window has closed.
 * If preview is still active, renders children normally.
 */
export function PreviewGuard({ children }) {
  const { user } = useAuth();
  const s = previewStatus(user);
  if (s.isExpired) return <PreviewEndedScreen />;
  return children;
}
